import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Info, Calendar, X } from "lucide-react";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, addDays, isWithinInterval, startOfDay, endOfDay, startOfWeek, endOfWeek, isBefore, startOfDay as startOfDayFn } from "date-fns";
import { de } from "date-fns/locale";
import { checkAvailability } from "@/utils/api";
import { parseBackendDateTime } from "@/utils/timezone";
import { isDateBookable, getMaxBookableDate } from "@/utils/validation";

/**
 * Kalender-Komponente für die Buchungsauswahl
 * Zeigt einen Monatskalender mit echten Verfügbarkeitsdaten
 * Ermöglicht die Navigation zwischen Monaten und die Auswahl von Zeiträumen
 * Verhindert die Auswahl vergangener Daten
 * 
 * @param {Object} props - Komponenten-Props
 * @param {Function} props.onDateRangeSelect - Callback für ausgewählte Zeiträume
 * @param {Object} props.selectedDateRange - Bereits ausgewählter Zeitraum
 */
export default function BookingCalendar({ onDateRangeSelect, selectedDateRange, availabilityData = [], loading = false, onMonthChange }) {
  // Aktuell angezeigter Monat
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Ausgewählter Zeitraum (temporär für die Vorschau)
  const [tempDateRange, setTempDateRange] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);


  // Berechne Start und Ende des aktuellen Monats
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  
  // Berechne Start und Ende der Kalenderwoche, die den Monat enthält
  // startOfWeek mit { weekStartsOn: 1 } startet die Woche mit Montag
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  
  // Generiere alle Tage des Kalenders (inkl. Tage vor/nach dem Monat)
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Aktueller Tag (heute) für Vergleich
  const today = startOfDayFn(new Date());

  /**
   * Prüft ob ein Tag in der Vergangenheit liegt
   * @param {Date} day - Zu prüfender Tag
   * @returns {boolean} true wenn der Tag in der Vergangenheit liegt
   */
  const isPastDate = (day) => {
    return isBefore(startOfDayFn(day), today);
  };

  // Maximales Enddatum für Anfragen (1 Jahr im Voraus)
  const maxBookableDate = getMaxBookableDate();
  const canGoNextMonth = startOfMonth(addMonths(currentMonth, 1)) <= maxBookableDate;

  /**
   * Prüft ob ein Tag auswählbar ist
   * Berücksichtigt vergangene Daten, 3-Tage-Vorlauf und Max. 1 Jahr Voraus
   * @param {Date} day - Zu prüfender Tag
   * @returns {boolean} true wenn der Tag auswählbar ist
   */
  const isSelectable = (day) => {
    if (isPastDate(day)) return false;
    
    // Prüfe Vorlauf-Regeln (min. 3 Tage / max. 1 Jahr)
    const bookableCheck = isDateBookable(day, day);
    return bookableCheck.isBookable;
  };

  /**
   * Bestimmt den Status eines Tages basierend auf den Verfügbarkeitsdaten
   * Berücksichtigt virtuelle Wochenend-Buchungen
   * @param {Date} day - Tag für den der Status bestimmt werden soll
   * @returns {Object} Status-Objekt mit type und conflicts
   */
  const getDayStatus = (day) => {
    const dayStart = startOfDay(day);
    const dayEnd = endOfDay(day);
    const dayOfWeek = day.getDay();
    const isWeekendDay = dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0;
    
    const conflicts = availabilityData.filter(booking => {
      // Konvertiere UTC-Zeiten vom Backend in lokale Zeit
      const bookingStart = parseBackendDateTime(booking.start);
      const bookingEnd = parseBackendDateTime(booking.end);
      
      // Überspringe ungültige Daten
      if (!bookingStart || !bookingEnd) return false;
      
      // Prüfe auf Überlappung
      const overlaps = dayStart < bookingEnd && dayEnd > bookingStart;
      
      // Wenn es eine virtuelle Wochenend-Buchung ist, zeige sie nur für Fr-So
      if (booking.isWeekendExpansion) {
        return overlaps && isWeekendDay;
      }
      
      // Wenn es eine normale Buchung ist und wir am Wochenende sind,
      // prüfe ob es eine virtuelle Wochenend-Buchung für diese Woche gibt
      if (isWeekendDay && overlaps) {
        // Prüfe ob es eine virtuelle Wochenend-Buchung für diesen Tag gibt
        const hasWeekendExpansion = availabilityData.some(b => {
          if (!b.isWeekendExpansion) return false;
          const expStart = parseBackendDateTime(b.start);
          const expEnd = parseBackendDateTime(b.end);
          if (!expStart || !expEnd) return false;
          return dayStart < expEnd && dayEnd > expStart;
        });
        
        // Wenn es eine virtuelle Wochenend-Buchung gibt, verstecke die ursprüngliche Buchung
        if (hasWeekendExpansion) {
          return false;
        }
      }
      
      return overlaps;
    });

    if (conflicts.length === 0) {
      return { type: 'free', conflicts: [] };
    }

    const hasAccepted = conflicts.some(c => c.state === 'accepted');
    const hasRequested = conflicts.some(c => c.state === 'requested');

    if (hasAccepted && hasRequested) {
      return { type: 'mixed', conflicts };
    } else if (hasAccepted) {
      return { type: 'booked', conflicts };
    } else {
      return { type: 'requested', conflicts };
    }
  };

  /**
   * Bestimmt die CSS-Klasse für den Status eines Tages
   * @param {Object} status - Status-Objekt vom getDayStatus
   * @returns {string} CSS-Klasse für die Hintergrundfarbe
   */
  const getStatusColor = (status) => {
    switch (status.type) {
      case 'free': return 'bg-green-500';
      case 'mixed': return 'bg-orange-500';
      case 'booked': return 'bg-red-500';
      case 'requested': return 'bg-purple-500';
      default: return 'bg-gray-200';
    }
  };

  /**
   * Behandelt das Klicken auf einen Tag
   * Startet oder erweitert die Zeitraumauswahl
   * Verhindere Auswahl vergangener Daten
   * @param {Date} day - Angeklickter Tag
   */
  const handleDayClick = (day) => {
    // Verhindere Auswahl vergangener Daten
    if (isPastDate(day)) {
      return;
    }

    if (!tempDateRange) {
      // Erster Klick - Start des Zeitraums
      setTempDateRange({ start: day, end: day });
    } else {
      // Zweiter Klick - Ende des Zeitraums
      const start = tempDateRange.start <= day ? tempDateRange.start : day;
      const end = tempDateRange.start <= day ? day : tempDateRange.start;
      
      // Prüfe Vorlauf-Regeln (min. 3 Tage / max. 1 Jahr) für den gesamten Zeitraum
      const bookableCheck = isDateBookable(start, end);
      if (!bookableCheck.isBookable) {
        // Zeige Warnung für nicht buchbare Zeiträume
        alert(bookableCheck.reason);
        setTempDateRange(null);
        return;
      }
      
      // Prüfe Verfügbarkeit
      const availability = checkAvailability(start, end, availabilityData);
      
      // Erlaube Auswahl auch bei Konflikten - die Warnung wird im Formular angezeigt
      onDateRangeSelect({ start, end });
      setTempDateRange(null);
    }
  };

  /**
   * Behandelt Keyboard-Navigation im Kalender
   * Ermöglicht Navigation mit Pfeiltasten und Auswahl mit Enter/Space
   * @param {KeyboardEvent} e - Keyboard-Event
   * @param {Date} day - Aktueller Tag
   */
  const handleKeyDown = (e, day) => {
    const days = eachDayOfInterval({
      start: startOfMonth(currentMonth),
      end: endOfMonth(currentMonth)
    });

    const currentIndex = days.findIndex(d => isSameDay(d, day));
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        newIndex = Math.min(currentIndex + 1, days.length - 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = Math.max(currentIndex - 1, 0);
        break;
      case 'ArrowDown':
        e.preventDefault();
        newIndex = Math.min(currentIndex + 7, days.length - 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        newIndex = Math.max(currentIndex - 7, 0);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleDayClick(day);
        return;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = days.length - 1;
        break;
      default:
        return;
    }

    // Fokussiere den neuen Tag
    const newDay = days[newIndex];
    const newButton = document.querySelector(`[data-day="${format(newDay, 'yyyy-MM-dd')}"]`);
    if (newButton) {
      newButton.focus();
    }
  };

  /**
   * Behandelt das Hover über einen Tag
   * Zeigt Vorschau des möglichen Zeitraums
   * @param {Date} day - Tag über dem gehovert wird
   */
  const handleDayHover = (day) => {
    if (tempDateRange && !isPastDate(day)) {
      setHoveredDate(day);
    }
  };

  /**
   * Behandelt das Verlassen eines Tags
   * Entfernt die Hover-Vorschau
   */
  const handleDayLeave = () => {
    setHoveredDate(null);
  };

  /**
   * Prüft ob ein Tag im ausgewählten oder vorgeschlagenen Zeitraum liegt
   * @param {Date} day - Zu prüfender Tag
   * @returns {boolean} true wenn der Tag im Zeitraum liegt
   */
  const isInSelectedRange = (day) => {
    if (selectedDateRange) {
      return isWithinInterval(day, { start: selectedDateRange.start, end: selectedDateRange.end });
    }
    
    if (tempDateRange && hoveredDate) {
      const start = tempDateRange.start <= hoveredDate ? tempDateRange.start : hoveredDate;
      const end = tempDateRange.start <= hoveredDate ? hoveredDate : tempDateRange.start;
      return isWithinInterval(day, { start, end });
    }
    
    return false;
  };

  /**
   * Setzt die Zeitraumauswahl zurück
   */
  const clearSelection = () => {
    setTempDateRange(null);
    setHoveredDate(null);
  };

  /**
   * Formatierung der Buchungszeiten
   * @param {Array} conflicts - Array von Konflikten/Buchungen
   * @param {Date} currentDay - Der aktuelle Tag im Kalender
   * @returns {Array} Array von formatierten Zeitstrings
   */
  const formatBookingTimes = (conflicts, currentDay) => {
    if (!conflicts || conflicts.length === 0) return [];
    
    return conflicts.map(booking => {
      // Wenn es eine virtuelle Wochenend-Buchung ist, zeige "ganztägig"
      if (booking.isWeekendExpansion) {
        return 'ganztägig';
      }
      
      // Konvertiere UTC-Zeiten vom Backend in lokale Zeit
      const start = parseBackendDateTime(booking.start);
      const end = parseBackendDateTime(booking.end);
      
      // Überspringe ungültige Daten
      if (!start || !end) return '';
      
      // Wenn es sich um denselben Tag handelt, zeige nur die Uhrzeiten
      if (isSameDay(start, end)) {
        return `${format(start, 'HH:mm', { locale: de })}-${format(end, 'HH:mm', { locale: de })}`;
      }
      
      // Bei mehrtägigen Buchungen zeige spezielle Texte
      if (isSameDay(start, currentDay)) {
        return `ab ${format(start, 'HH:mm', { locale: de })} Uhr`;
      } else if (isSameDay(end, currentDay)) {
        return `bis ${format(end, 'HH:mm', { locale: de })} Uhr`;
      } else {
        return 'ganztägig';
      }
    }).filter(timeString => timeString !== ''); // Entferne leere Strings
  };

  /**
   * Bestimmt die Hintergrundfarbe für eine spezifische Buchung
   * @param {Object} booking - Buchungsobjekt
   * @returns {string} CSS-Klasse für die Hintergrundfarbe
   */
  const getBookingColor = (booking) => {
    switch (booking.state) {
      case 'accepted': return 'bg-red-100 text-red-800 border-red-300';
      case 'requested': return 'bg-orange-100 text-orange-800 border-orange-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Kalender Header mit Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            const newMonth = subMonths(currentMonth, 1);
            setCurrentMonth(newMonth);
            // Benachrichtige Parent-Komponente über Monatsänderung
            if (onMonthChange) {
              onMonthChange(newMonth);
            }
          }}
          aria-label="Vorheriger Monat"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        
        <h3 className="text-2xl font-semibold">
          {format(currentMonth, 'MMMM yyyy', { locale: de })}
        </h3>
        
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            if (!canGoNextMonth) return;
            const newMonth = addMonths(currentMonth, 1);
            setCurrentMonth(newMonth);
            // Benachrichtige Parent-Komponente über Monatsänderung
            if (onMonthChange) {
              onMonthChange(newMonth);
            }
          }}
          disabled={!canGoNextMonth}
          aria-label="Nächster Monat"
          title={!canGoNextMonth ? 'Anfragen sind maximal 1 Jahr im Voraus möglich' : undefined}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Aktueller Zeitraum */}
      {selectedDateRange && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                <span className="font-medium">Ausgewählter Zeitraum:</span>
                <Badge variant="outline" className="text-lg px-4 py-2">
                  {format(selectedDateRange.start, 'dd.MM.yyyy', { locale: de })} - {format(selectedDateRange.end, 'dd.MM.yyyy', { locale: de })}
                </Badge>
              </div>
              <Button variant="outline" size="sm" onClick={() => onDateRangeSelect(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hauptkalender */}
      <Card>
        <CardContent className="p-6">
          <div id="calendar-instructions" className="sr-only">
            Verwenden Sie die Pfeiltasten zur Navigation zwischen den Tagen. 
            Drücken Sie Enter oder Leertaste, um einen Tag auszuwählen. 
            Verwenden Sie Home und End, um zum Anfang oder Ende des Monats zu springen.
          </div>
          {/* Wochentage-Header */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
              <div key={day} className="text-center font-medium text-gray-500 p-2">
                {day}
              </div>
            ))}
          </div>

          {/* Kalendertage */}
          <div 
            className="grid grid-cols-7 gap-2"
            role="grid"
            aria-label={`Kalender für ${format(currentMonth, 'MMMM yyyy', { locale: de })}`}
            aria-describedby="calendar-instructions"
          >
            {days.map(day => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isCurrentDay = isToday(day);
              const isPast = isPastDate(day);
              const isSelectableDay = isSelectable(day);
              const bookableCheck = isDateBookable(day, day);
              const isTooSoon = !bookableCheck.isBookable && bookableCheck.code === 'TOO_SOON' && !isPast;
              const isTooFar = !bookableCheck.isBookable && bookableCheck.code === 'TOO_FAR' && !isPast;
              const status = getDayStatus(day);
              const isSelected = isInSelectedRange(day);
              const isStart = tempDateRange && isSameDay(day, tempDateRange.start);
              const isEnd = tempDateRange && hoveredDate && isSameDay(day, hoveredDate);

              return (
                <button
                  key={day.toString()}
                  data-day={format(day, 'yyyy-MM-dd')}
                  className={`
                    relative aspect-square p-2 rounded-lg border-2 transition-all duration-200 calendar-day
                    ${!isCurrentMonth ? 'text-gray-500 border-gray-100' : 'text-gray-700 border-gray-200'}
                    ${isCurrentDay ? 'font-bold border-black bg-blue-50' : ''}
                    ${isSelected ? 'bg-blue-100 border-blue-400 shadow-md' : ''}
                    ${isStart ? 'bg-green-200 border-green-500 shadow-lg ring-2 ring-green-300' : ''}
                    ${isEnd ? 'bg-green-300 border-green-600 shadow-lg ring-2 ring-green-400' : ''}
                    ${isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}
                    ${isTooSoon ? 'bg-yellow-50 text-yellow-700 border-yellow-200 cursor-not-allowed' : ''}
                    ${isTooFar ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : ''}
                    ${isSelectableDay ? 'cursor-pointer hover:border-blue-300 hover:shadow-sm hover:bg-blue-50' : ''}
                  `}
                  onClick={() => isCurrentMonth && isSelectableDay && handleDayClick(day)}
                  onKeyDown={(e) => isCurrentMonth && isSelectableDay && handleKeyDown(e, day)}
                  onMouseEnter={() => isCurrentMonth && isSelectableDay && handleDayHover(day)}
                  onMouseLeave={handleDayLeave}
                  disabled={!isSelectableDay}
                  aria-label={`${format(day, 'dd. MMMM yyyy', { locale: de })}${isPast ? ' - Vergangen' : ''}${isTooSoon ? ' - Zu kurzfristig' : ''}${isTooFar ? ' - Zu weit im Voraus' : ''}${status.conflicts.length > 0 ? ` - ${status.conflicts.length} Buchung(en)` : ''}`}
                  title={
                    isPast ? 'Vergangene Daten können nicht ausgewählt werden' :
                    isTooSoon || isTooFar ? bookableCheck.reason :
                    'Verwenden Sie Pfeiltasten zur Navigation, Enter oder Leertaste zur Auswahl'
                  }
                >
                  <span className="text-sm">{format(day, 'd')}</span>
                  
                  {/* Verfügbarkeits-Indikatoren */}
                  {isCurrentMonth && isSelectableDay && status.conflicts.length > 0 && (
                    <div className="absolute bottom-1 left-1 right-1 flex justify-center gap-0.5 pointer-events-none">
                      {/* Mobile: Einfache Punkte als Indikatoren */}
                      <div className="sm:hidden flex gap-0.5">
                        {status.conflicts.length > 3 ? (
                          <>
                            {status.conflicts.slice(0, 2).map((booking, index) => (
                              <div
                                key={index}
                                className={`
                                  w-1.5 h-1.5 rounded-full
                                  ${booking.state === 'accepted' ? 'bg-red-600' : 'bg-orange-600'}
                                `}
                              />
                            ))}
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-600" title={`${status.conflicts.length - 2} weitere Buchungen`} />
                          </>
                        ) : (
                          status.conflicts.map((booking, index) => (
                            <div
                              key={index}
                              className={`
                                w-1.5 h-1.5 rounded-full
                                ${booking.state === 'accepted' ? 'bg-red-600' : 'bg-orange-600'}
                              `}
                            />
                          ))
                        )}
                      </div>
                      
                      {/* Desktop: Vollständige Zeitanzeigen */}
                      <div className="hidden sm:flex sm:flex-col sm:gap-1 sm:w-full">
                        {status.conflicts.length > 3 ? (
                          <>
                            {formatBookingTimes(status.conflicts.slice(0, 2), day).map((timeString, index) => (
                              <div
                                key={index}
                                className={`
                                  text-xs px-1 py-0.5 rounded text-center font-medium
                                  ${getBookingColor(status.conflicts[index])}
                                  border border-current border-opacity-20
                                `}
                              >
                                {timeString}
                              </div>
                            ))}
                            <div
                              className="text-xs px-1 py-0.5 rounded text-center font-medium"
                              title={`${status.conflicts.length - 2} weitere Buchungen`}
                            >
                              ...
                            </div>
                          </>
                        ) : (
                          formatBookingTimes(status.conflicts, day).map((timeString, index) => (
                            <div
                              key={index}
                              className={`
                                text-xs px-1 py-0.5 rounded text-center font-medium
                                ${getBookingColor(status.conflicts[index])}
                                border border-current border-opacity-20
                              `}
                            >
                              {timeString}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legende für Buchungsstatus */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4 text-primary" />
            <span className="font-medium text-sm text-primary">Legende</span>
          </div>
          
          {/* Erklärung der Zeitanzeigen */}
          <div className="text-xs text-muted-foreground space-y-2">
            {/* Desktop Legende */}
            <div className="hidden sm:flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-xs border border-red-300">09:00-12:00</span>
                <span>= Raum belegt</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded text-xs border border-orange-300">14:00-18:00</span>
                <span>= Angefragte Buchung</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2 bg-yellow-50 border-yellow-200" aria-label="Zu kurzfristig"></div>
                <span>= Zu kurzfristig</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2 bg-slate-100 border-slate-200" aria-label="Zu weit im Voraus"></div>
                <span>= Zu weit im Voraus (max. 1 Jahr)</span>
              </div>
            </div>
            
            {/* Mobile Legende */}
            <div className="sm:hidden space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-600"></div>
                <span>= Raum belegt</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-600"></div>
                <span>= Angefragte Buchung</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded border bg-yellow-50 border-yellow-200" aria-label="Zu kurzfristig"></div>
                <span>= Zu kurzfristig</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded border bg-slate-100 border-slate-200" aria-label="Zu weit im Voraus"></div>
                <span>= Zu weit im Voraus (max. 1 Jahr)</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Anweisungen für die Zeitraumauswahl */}
      <Card>
        <CardContent className="p-4">
          <div className="text-center space-y-3">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Info className="w-4 h-4" />
              <span>Klicken Sie auf einen Tag um den Zeitraum zu starten, dann auf einen zweiten Tag um ihn zu beenden.</span>
            </div>
            
            <div className="text-xs text-gray-500">
              Vergangene Daten können nicht ausgewählt werden. Anfragen sind maximal 1 Jahr im Voraus möglich.
            </div>
            
            {tempDateRange && (
              <div className="flex items-center justify-center gap-4">
                <Badge variant="outline" className="px-4 py-2">
                  Start: {format(tempDateRange.start, 'dd.MM.yyyy', { locale: de })}
                </Badge>
                <Button variant="outline" size="sm" onClick={clearSelection}>
                  <X className="w-4 h-4" />
                  Zurücksetzen
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lade-Indikator */}
      {loading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Lade Verfügbarkeitsdaten...</p>
        </div>
      )}
    </div>
  );
}